/**
 * 전역 SMS 재인증 창 — **어느 화면에서 막혀도 여기서 끝낸다.**
 *
 * ━━━ 왜 필요한가 (2026-09-22 · Codex 심사 [C-9]) ━━━
 *
 * 2단계 계획 1판은 거절 **계약**만 설계하고 **그걸 받는 화면**을 안 만들었다.
 *
 *   `SMSVerification` 은 로그인·가입 화면에만 붙어 있었다.
 *   업무 화면에서 재인증이 필요해지면 **SMS 를 할 곳이 없다.**
 *   → 토큰은 지켜지는데 아무것도 안 되는 상태. 「부당한 로그아웃」보다 나쁘다.
 *
 * 그래서 이 창이 앱 최상단(라우터 바깥)에 산다. 재발급이 `sms_required` 를 받으면
 * `reauthBus` 를 통해 신호가 오고, 여기서 인증을 마치면 화면이 그대로 이어진다.
 *
 * ━━━ ⛔ 이 창이 지키는 것 ━━━
 *
 *   ① **닫을 수 없다.** 배경을 눌러도 안 닫힌다 — 인증을 마치거나 로그아웃뿐이다.
 *      어중간하게 닫으면 「아무것도 안 되는 화면」에 사람을 두게 된다.
 *   ② 다른 탭이 먼저 마치면 **스스로 닫힌다** (`reauthBus` 가 storage 이벤트를 본다).
 *   ③ 챌린지가 없으면 **뜨지 않는다** — 인증할 수단이 없는 창은 띄우지 않는다.
 */

import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import SMSVerification from '../SMSVerification';
import {
  subscribeSmsReauth,
  clearSmsReauth,
  watchOtherTabs,
  type SmsReauthRequest,
} from '../../services/auth/reauthBus';
import { useAuth } from '../../context/AuthContext';
import { colors, spacing, typography, borderRadius, shadows } from '../../styles/design-tokens';

const Backdrop = styled.div.attrs({ className: 'ws-modal-backdrop' })`
  position: fixed;
  inset: 0;
  z-index: 2147483000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${spacing.lg};
  background: ${colors.overlay.default};
`;

const Panel = styled.div.attrs({ className: 'ws-modal' })`
  width: 100%;
  max-width: 420px;
  background: ${colors.background.light};
  border: 1px solid ${colors.border.default};
  border-radius: ${borderRadius.xl};
  box-shadow: ${shadows.lg};
  padding: ${spacing.xl};
`;

const Title = styled.h2`
  margin: 0 0 ${spacing.sm};
  font-size: ${typography.heading.h4.fontSize};
  font-weight: ${typography.heading.h4.fontWeight};
  color: ${colors.text.primary};
`;

const Desc = styled.p`
  margin: 0 0 ${spacing.lg};
  font-size: ${typography.body.base.fontSize};
  line-height: ${typography.body.base.lineHeight};
  color: ${colors.text.secondary};
`;

const Footer = styled.div`
  margin-top: ${spacing.lg};
  padding-top: ${spacing.md};
  border-top: 1px solid ${colors.border.default};
  display: flex;
  justify-content: flex-end;
`;

const LogoutLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-size: ${typography.body.small.fontSize};
  color: ${colors.text.secondary};
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    color: ${colors.text.primary};
  }
`;

const SmsReauthGate: React.FC = () => {
  const [요청, set요청] = useState<SmsReauthRequest | null>(null);
  const { logout } = useAuth();

  useEffect(() => subscribeSmsReauth(set요청), []);

  // ⛔ 다른 탭 감시는 **창이 떠 있을 때만이 아니라 항상** 건다.
  //    창이 없는 탭은 다른 탭의 성공을 영영 모르게 되기 때문이다.
  useEffect(() => watchOtherTabs(), []);

  if (!요청) return null;

  // ⛔ 전화번호가 없으면 인증번호를 보낼 곳이 없다. 창을 띄워 봐야 아무것도 못 한다.
  //    이때는 조용히 로그아웃시키는 편이 정직하다.
  if (!요청.phoneNumber) {
    void logout();
    clearSmsReauth();
    return null;
  }

  return (
    <Backdrop role="dialog" aria-modal="true" aria-label="보안 재인증">
      <Panel>
        <Title>보안 확인이 필요합니다</Title>
        <Desc>
          작업 내용은 그대로 있습니다. 문자로 받은 인증번호를 넣으면 하던 일을 계속할 수 있습니다.
        </Desc>

        <SMSVerification
          phoneNumber={요청.phoneNumber}
          purpose="login"
          smsChallenge={요청.challenge}
          onVerified={() => {
            // 인증이 끝나면 서버의 `last_sms_auth_at` 이 갱신된다.
            // 창을 닫으면 막혀 있던 요청이 다음 시도에서 통과한다.
            clearSmsReauth();
          }}
        />

        <Footer>
          <LogoutLink
            type="button"
            onClick={() => {
              clearSmsReauth();
              void logout();
            }}
          >
            로그아웃
          </LogoutLink>
        </Footer>
      </Panel>
    </Backdrop>
  );
};

export default SmsReauthGate;
